import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { mapPlaylistFromVideoSearch, Button, ToggleButtons, Dropdown, LoadingSpinner } from 'yankees-react-utils';
import BlockingChart from '../../../components/BlockingChart';
import NonDirtBlockingChart from '../../../components/NonDirtBlockingChart';
import { useOktaQueries, Player, PlayerReport } from '../../../data';
import ReportCard from '../../../components/ReportCard';
import VideoPlayer from '../../../components/VideoPlayer';
import { store } from '../../../store';

const StyledVideoContainer = styled.div`
    & card-body {
        margin: 0;
    }
`;
const StyledFiltersContainer = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    > div {
        margin: 5px;
        margin-left: auto;
    }
`;
const StyledReportCard = styled(ReportCard)`
    margin-top: 10px;
`;

const buildQueries = (playerid, gameid, gameinfo) => {
    const { levelid } = gameinfo;
    const queries = [
        {
            queryKey: ['player', playerid],
            queryFn: (t) => Player.get(t, { playerId: playerid }),
        },
        {
            queryKey: ['blockingReport', playerid, gameid],
            queryFn: (t) => PlayerReport.blok(t, {
                playerid,
                gameid,
                levelid,
            }),
        },
        {
            queryKey: ['blockingNonDirtReport', playerid, gameid],
            queryFn: (t) => PlayerReport.bloknondirt(t, {
                playerid,
                gameid,
                levelid,
            }),
        },
    ];
    return queries;
};

const mapDataResponse = (translate, response) => {
    const player = response[0].data || {};
    const blokData = response[1].data || {};
    const blokNonDirtData = response[2].data || {};

    const pitcherBlokOptions = [{ name: translate('sections.post-game.blocking.all-pitchers'), filterFn: null }];
    let pitchers = blokData.events.map((p, index) => ({ pitcherid: p.pitcherid, pitcher: p.pitchername, sortorder: index }));
    let uniquePitcherIds = [...new Set(pitchers.map((p) => p.pitcherid))];
    uniquePitcherIds
        .map((id) => pitchers.find((p) => p.pitcherid === id)).sort((a, b) => a.sortorder - b.sortorder)
        .forEach((p) => pitcherBlokOptions.push({ name: p.pitcher, filterFn: (x) => x.pitcherid === p.pitcherid }));

    const pitcherBlokNonDirtOptions = [{ name: translate('sections.post-game.blocking.all-pitchers'), filterFn: null }];
    pitchers = blokNonDirtData.events.map((p, index) => ({ pitcherid: p.pitcherid, pitcher: p.pitchername, sortorder: index }));
    uniquePitcherIds = [...new Set(pitchers.map((p) => p.pitcherid))];
    uniquePitcherIds
        .map((id) => pitchers.find((p) => p.pitcherid === id)).sort((a, b) => a.sortorder - b.sortorder)
        .forEach((p) => pitcherBlokNonDirtOptions.push({ name: p.pitcher, filterFn: (x) => x.pitcherid === p.pitcherid }));

    const batterBlokOptions = [{ name: translate('sections.post-game.blocking.all-batters'), filterFn: null }];
    let batters = blokData.events.map((b, index) => ({ batterid: b.batterid, batter: b.battername, sortorder: index }));
    let uniqueBatterIds = [...new Set(batters.map((b) => b.batterid))];
    uniqueBatterIds
        .map((id) => batters.find((b) => b.batterid === id)).sort((a, b) => a.sortorder - b.sortorder)
        .forEach((p) => batterBlokOptions.push({ name: p.batter, filterFn: (x) => x.batterid === p.batterid }));

    const batterBlokNonDirtOptions = [{ name: translate('sections.post-game.blocking.all-batters'), filterFn: null }];
    batters = blokNonDirtData.events.map((b, index) => ({ batterid: b.batterid, batter: b.battername, sortorder: index }));
    uniqueBatterIds = [...new Set(batters.map((b) => b.batterid))];
    uniqueBatterIds
        .map((id) => batters.find((b) => b.batterid === id)).sort((a, b) => a.sortorder - b.sortorder)
        .forEach((p) => batterBlokNonDirtOptions.push({ name: p.batter, filterFn: (x) => x.batterid === p.batterid }));

    return {
        player,
        blokData,
        blokNonDirtData,
        pitcherBlokOptions,
        pitcherBlokNonDirtOptions,
        batterBlokOptions,
        batterBlokNonDirtOptions,
    };
};

const filterPitches = (blokPitches, blokNonDirtPitches, filters) => {
    const { pitcherBlok, pitcherBlokNonDirt, batterBlok, batterBlokNonDirt, blokResult, blokNonDirtResult } = filters;
    const filteredBlokPitches = blokPitches.filter((p) => (!pitcherBlok.filterFn || pitcherBlok.filterFn(p))
        && (!batterBlok.filterFn || batterBlok.filterFn(p)) && (!blokResult.filterFn || blokResult.filterFn(p)));
    const filteredBlokNonDirtPitches = blokNonDirtPitches.filter((p) => (!pitcherBlokNonDirt.filterFn || pitcherBlokNonDirt.filterFn(p))
        && (!batterBlokNonDirt.filterFn || batterBlokNonDirt.filterFn(p)) && (!blokNonDirtResult.filterFn || blokNonDirtResult.filterFn(p)));
    return { filteredBlokPitches, filteredBlokNonDirtPitches };
};

const getCatcherVideo = (events = [], user) => {
    const playlist = mapPlaylistFromVideoSearch({ videos: events, playervideos: [] }, user.Roles.map((r) => r.RoleName));
    return playlist;
};

const getPitchVideo = (events = [], pitchInfo = {}, user) => {
    console.log(user.Roles.map((r) => r.RoleName));
    const { gameid, pitchseq, eventseq } = pitchInfo;
    const event = events.find((e) => e.gameid === gameid && e.pitchseq === pitchseq && e.eventseq === eventseq);
    return mapPlaylistFromVideoSearch({ videos: [event], playervideos: [] }, user.Roles.map((r) => r.RoleName));
};

const VIEW = { DIRT: 'dirt', NONDIRT: 'nondirt' };
const viewOptions = (translate) => [
    { name: translate('sections.post-game.blocking.dirt-pitches'), value: VIEW.DIRT, variant: 'default' },
    { name: translate('sections.post-game.blocking.non-dirt-pitches'), value: VIEW.NONDIRT, variant: 'default' },
];

const blokResultOptions = (translate) => [
    { name: translate('sections.post-game.blocking.all-pitch-results'), filterFn: null },
    { name: translate('sections.post-game.blocking.runners-advanced'), filterFn: (x) => x.advr === 1 },
    { name: translate('sections.post-game.blocking.runners-not-advanced'), filterFn: (x) => x.advr === 0 },
];

const Blocking = ({ playerid, gameid, gameinfo }) => {
    const { state: { user } } = useContext(store);
    const { t: translate } = useTranslation();
    const [view, setView] = useState(VIEW.DIRT);
    const [filters, setFilters] = useState({
        pitcherBlok: { name: translate('sections.post-game.blocking.all-pitchers'), filterFn: null },
        pitcherBlokNonDirt: { name: translate('sections.post-game.blocking.all-pitchers'), filterFn: null },
        blokResult: blokResultOptions(translate)[0],
        batterBlok: { name: translate('sections.post-game.blocking.all-batters'), filterFn: null },
        batterBlokNonDirt: { name: translate('sections.post-game.blocking.all-batters'), filterFn: null },
        blokNonDirtResult: blokResultOptions(translate)[0],
    });
    const [playlist, setPlaylist] = useState([]);

    const dataQueries = buildQueries(playerid, gameid, gameinfo);
    const dataQueriesResponse = useOktaQueries(dataQueries);

    if (dataQueriesResponse.some((q) => q.isLoading)) {
        return <LoadingSpinner />;
    }

    const { blokData, blokNonDirtData, pitcherBlokOptions, pitcherBlokNonDirtOptions, batterBlokOptions, batterBlokNonDirtOptions } = mapDataResponse(translate, dataQueriesResponse);
    const { filteredBlokPitches, filteredBlokNonDirtPitches } = filterPitches(blokData.events, blokNonDirtData.events, filters);
    const allVideosPlaylist = view === VIEW.DIRT ? getCatcherVideo(filteredBlokPitches, user) : getCatcherVideo(filteredBlokNonDirtPitches, user);

    return (
        <>
            <Row>
                <Col xl={{ span: 4, order: 1 }} md={{ span: 12, order: 2 }}>
                    <StyledReportCard>
                        <ToggleButtons name="blocking" options={viewOptions(translate)} onChange={(value) => setView(value)} defaultValue={VIEW.DIRT} />
                        <br /><hr />
                        {view === VIEW.DIRT && (
                            <BlockingChart
                                events={filteredBlokPitches}
                                isGroup={false}
                                onClick={(pitchInfo) => setPlaylist(getPitchVideo(blokData.events, pitchInfo, user))}
                            />
                        )}
                        {view === VIEW.NONDIRT && (
                            <NonDirtBlockingChart
                                pitches={filteredBlokNonDirtPitches}
                                onClick={(pitchInfo) => setPlaylist(getPitchVideo(blokNonDirtData.events, pitchInfo, user))}
                            />
                        )}
                        <StyledFiltersContainer>
                            {view === VIEW.DIRT && (
                                <>
                                    <Dropdown
                                        options={pitcherBlokOptions}
                                        value={filters.pitcherBlok}
                                        onSelect={(value) => setFilters({ ...filters, pitcherBlok: value })}
                                    />
                                    <Dropdown
                                        options={batterBlokOptions}
                                        value={filters.batterBlok}
                                        onSelect={(value) => setFilters({ ...filters, batterBlok: value })}
                                    />
                                    <Dropdown
                                        options={blokResultOptions(translate)}
                                        value={filters.blokResult}
                                        onSelect={(value) => setFilters({ ...filters, blokResult: value })}
                                    />
                                </>
                            )}
                            {view === VIEW.NONDIRT && (
                                <>
                                    <Dropdown
                                        options={pitcherBlokNonDirtOptions}
                                        value={filters.pitcherBlokNonDirt}
                                        onSelect={(value) => setFilters({ ...filters, pitcherBlokNonDirt: value })}
                                    />
                                    <Dropdown
                                        options={batterBlokNonDirtOptions}
                                        value={filters.batterBlokNonDirt}
                                        onSelect={(value) => setFilters({ ...filters, batterBlokNonDirt: value })}
                                    />
                                    <Dropdown
                                        options={blokResultOptions(translate)}
                                        value={filters.blokNonDirtResult}
                                        onSelect={(value) => setFilters({ ...filters, blokNonDirtResult: value })}
                                    />
                                </>
                            )}
                            <div><Button variant="primary" onClick={() => setPlaylist(allVideosPlaylist)}>{translate('sections.post-game.blocking.play-videos')}</Button></div>
                        </StyledFiltersContainer>
                    </StyledReportCard>
                </Col>
                <Col xl={{ span: 8, order: 2 }} md={{ span: 12, order: 1 }}>
                    <StyledReportCard body={!playlist?.length}>
                        <StyledVideoContainer>
                            {playlist?.length ? <VideoPlayer playlist={playlist} maxHeight={640} /> : <h4>{translate('sections.post-game.blocking.no-videos-selected')}</h4>}
                        </StyledVideoContainer>
                    </StyledReportCard>
                </Col>
            </Row>
        </>
    );
};

Blocking.propTypes = {
    playerid: PropTypes.number.isRequired,
    gameid: PropTypes.number.isRequired,
    gameinfo: PropTypes.shape({
        gameid: PropTypes.number,
        gamedate: PropTypes.string,
        levelid: PropTypes.number,
    }).isRequired,
};

export default Blocking;
